/**
 * uPlot scatter lasso coordinate helpers (no store/worker imports — safe for unit tests).
 */
import type uPlot from "uplot";

export interface UPlotLassoPointInput {
  id: string;
  x: number;
  y: number;
}

/**
 * Map a data (x, y) to overlay viewBox coords (plot area origin, CSS px).
 * valToPos(..., true) is canvas-root device px; overlay is anchored at u.bbox.
 */
export function uPlotValToOverlayCoords(
  u: uPlot,
  dataX: number,
  dataY: number,
  pxRatio: number
): { x: number; y: number } | null {
  const cx = u.valToPos(dataX, "x", true);
  const cy = u.valToPos(dataY, "y", true);
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) {return null;}
  if (!(pxRatio > 0)) {return null;}
  return {
    x: (cx - u.bbox.left) / pxRatio,
    y: (cy - u.bbox.top) / pxRatio,
  };
}

/**
 * Map data coords to viewport (client) via overlay SVG CTM.
 * Aligns with d3-lasso polygon coords (clientX/clientY).
 */
export function buildUPlotOverlayLassoPoints(
  data: Record<string, unknown>[],
  overlay: SVGSVGElement,
  toPlotOverlayCoords: (x: number, y: number) => { x: number; y: number } | null,
  getPointId: (row: Record<string, unknown>) => string | null
): { points: UPlotLassoPointInput[]; rowsById: Map<string, Record<string, unknown>> } {
  const ctm = overlay.getScreenCTM();
  if (!ctm) {return { points: [], rowsById: new Map() };}

  const points: UPlotLassoPointInput[] = [];
  const rowsById = new Map<string, Record<string, unknown>>();

  for (const d of data) {
    const id = getPointId(d);
    if (!id) {continue;}
    const x = Number(d.x);
    const y = Number(d.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {continue;}
    const plot = toPlotOverlayCoords(x, y);
    if (!plot) {continue;}
    points.push({
      id,
      x: ctm.a * plot.x + ctm.c * plot.y + ctm.e,
      y: ctm.b * plot.x + ctm.d * plot.y + ctm.f,
    });
    rowsById.set(id, d);
  }

  return { points, rowsById };
}
